// Promise wrapper around the src/worker.ts request/response protocol. Takes
// any Worker-like object (a real `Worker`, a `worker_threads` bridge, or a
// test double) so this module stays usable from both the browser bundle and
// Node-side tests.

import type { Master } from './derive.js';
import type { SealedEnvelope, SealedMaster } from './master-storage.js';
import type { RelayerSigner } from './relayer.js';
import type { SweepPayer, WorkerErrorCode, WorkerRequest, WorkerResponse } from './worker.js';

// Re-exported for the app, which reaches the worker's types only through the package index.
export type { SweepPayer, WorkerErrorCode } from './worker.js';

/** The subset of the DOM `Worker` API this client needs. */
export interface WorkerLike {
  postMessage(message: WorkerRequest): void;
  addEventListener?(type: 'message', listener: (event: { data: WorkerResponse }) => void): void;
  // Declared through a method type so a DOM `Worker` (whose `onmessage` takes a full MessageEvent) is assignable under strictFunctionTypes.
  onmessage?: { bivariant(event: { data: WorkerResponse }): void }['bivariant'] | null;
}

/** A worker's error response; `code` is the worker's typed refusal (docs/18 2.4), 'Unknown' when no rule names it. */
export class WorkerClientError extends Error {
  constructor(
    message: string,
    readonly code: WorkerErrorCode = 'Unknown',
  ) {
    super(message);
    this.name = 'WorkerClientError';
  }
}

interface Pending {
  resolve: (value: WorkerResponse) => void;
  reject: (err: Error) => void;
}

/** Typed client for one worker: derives trees, signs against them, wipes them, and drives the fee key (docs/18 2.4). */
export class WorkerClient {
  #worker: WorkerLike;
  #nextId = 1;
  #pending = new Map<number, Pending>();

  constructor(worker: WorkerLike) {
    this.#worker = worker;
    const onMessage = (event: { data: WorkerResponse }) => this.#onMessage(event.data);
    if (worker.addEventListener) worker.addEventListener('message', onMessage);
    else worker.onmessage = onMessage;
  }

  #onMessage(response: WorkerResponse): void {
    const pending = this.#pending.get(response.id);
    if (!pending) return;
    this.#pending.delete(response.id);
    if (response.type === 'error') pending.reject(new WorkerClientError(response.message, response.code ?? 'Unknown'));
    else pending.resolve(response);
  }

  #send<R extends WorkerResponse>(message: WorkerRequest): Promise<R> {
    return new Promise((resolve, reject) => {
      this.#pending.set(message.id, { resolve: resolve as (value: WorkerResponse) => void, reject });
      this.#worker.postMessage(message);
    });
  }

  /** Derive tree `treeIndex` in the worker; returns the genesis verifying key and the derivation time. */
  async deriveTree(master: Master, programId: Uint8Array, treeIndex: number): Promise<{ verifyingKey: Uint8Array; ms: number }> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').TreeResponse>({ id, type: 'deriveTree', master, programId, treeIndex });
    return { verifyingKey: res.verifyingKey, ms: res.ms };
  }

  /** Open a sealed master inside the worker; the worker holds the result until 'wipe'. Rejects on a wrong passphrase or a tampered envelope. */
  async unseal(sealed: SealedMaster, passphrase: string): Promise<void> {
    const id = this.#nextId++;
    await this.#send<import('./worker.js').UnsealedResponse>({ id, type: 'unseal', sealed, passphrase });
  }

  /** Create a phrase inside the worker and hold its master (docs/08 5.1 step 2). Returns the 24 words for display, once; the caller drops them after confirmation. */
  async generate(): Promise<string> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').GeneratedResponse>({ id, type: 'generate' });
    return res.mnemonic;
  }

  /** Hold the master of an existing phrase (docs/08 5.2). Rejects on a phrase that is not checksum-valid; the words are not kept. */
  async adopt(mnemonic: string): Promise<void> {
    const id = this.#nextId++;
    await this.#send<import('./worker.js').AdoptedResponse>({ id, type: 'adopt', mnemonic });
  }

  /** Seal the held master under the device passphrase (docs/08 5.1 step 4); the envelope is what the app stores. */
  async seal(passphrase: string, iterations?: number): Promise<SealedMaster> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').SealedResponse>({ id, type: 'seal', passphrase, iterations });
    return res.sealed;
  }

  /** Derive tree `treeIndex` from the master the worker holds (after `unseal`, `generate` or `adopt`); no master crosses the boundary. */
  async deriveHeldTree(programId: Uint8Array, treeIndex: number): Promise<{ verifyingKey: Uint8Array; ms: number }> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').TreeResponse>({ id, type: 'deriveTree', programId, treeIndex });
    return { verifyingKey: res.verifyingKey, ms: res.ms };
  }

  /** Zero and forget one tree the worker holds. */
  async wipeTree(treeIndex: number): Promise<void> {
    const id = this.#nextId++;
    await this.#send<import('./worker.js').WipedResponse>({ id, type: 'wipeTree', treeIndex });
  }

  /** Sign `digest` at `leaf` of a tree already derived in the worker. */
  async sign(treeIndex: number, leaf: number, digest: Uint8Array): Promise<{ signature: Uint8Array; saltTrial: number; ms: number }> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').SignatureResponse>({ id, type: 'sign', treeIndex, leaf, digest });
    return { signature: res.signature, saltTrial: res.saltTrial, ms: res.ms };
  }

  /** Zero and forget every tree the worker holds, the held master and both fee-key slots: the lock path. */
  async wipe(): Promise<void> {
    const id = this.#nextId++;
    await this.#send<import('./worker.js').WipedResponse>({ id, type: 'wipe' });
  }

  // ---------------------------------------------------------------- the fee key (docs/18-one-fee-reserve.md 2.4)

  /** The browser capability probe, a known-answer test (docs/18 2.4): resolves when this worker imports the signed-v1 fixture's seed non-extractable and reproduces its public key and signature byte for byte, and the signature verifies; rejects with code 'FeeKeyUnsupported' otherwise. */
  async feeProbe(): Promise<void> {
    const id = this.#nextId++;
    await this.#send<import('./worker.js').FeeProbeResponse>({ id, type: 'feeProbe' });
  }

  /** The public key of fee key `generation` (0 in this release) of the held master, under the scope compiled into the worker. The worker holds nothing afterwards. */
  async feeAddress(generation: number): Promise<Uint8Array> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').FeeAddressResponse>({ id, type: 'feeAddress', generation });
    return res.publicKey;
  }

  /** Install fee key `generation` as the worker's active payer; returns its public key. Idempotent for the same key. */
  async activateFeePayer(generation: number): Promise<Uint8Array> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').FeePayerResponse>({ id, type: 'activateFeePayer', generation });
    return res.publicKey;
  }

  /** The active payer's 64-byte signature over `message`, a serialised v1 message it pays for alone; the worker refuses any other (typed code). */
  async feeSign(message: Uint8Array): Promise<Uint8Array> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').FeeSignatureResponse>({ id, type: 'feeSign', message });
    return res.signature;
  }

  /** Open a legacy relayer envelope into the worker's sweep-only set; returns its public key. The seed never leaves the worker. */
  async openLegacy(sealed: SealedEnvelope, passphrase: string): Promise<Uint8Array> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').LegacyOpenedResponse>({ id, type: 'openLegacy', sealed, passphrase });
    return res.publicKey;
  }

  /** A finished sweep wire (message, then signatures): one System transfer of `lamports` from the sweep-only `source` into the active payer, Form A (`payer: 'source'`) or Form B (`payer: 'active'`). */
  async sweep(args: { source: Uint8Array; lamports: bigint; blockhash: Uint8Array; payer: SweepPayer }): Promise<Uint8Array> {
    const id = this.#nextId++;
    const res = await this.#send<import('./worker.js').SweepWireResponse>({ id, type: 'sweep', source: args.source, lamports: args.lamports, blockhash: args.blockhash, payer: args.payer });
    return res.wire;
  }
}

/**
 * The worker's active payer as a RelayerSigner (docs/18 section 9): `sign` is
 * the worker's `feeSign`, so the engine and `signV1` stay unchanged while the
 * fee key never leaves the worker. `publicKey` is the one `activateFeePayer`
 * returned; the worker checks the payer itself on every signature.
 */
export function workerFeeSigner(client: WorkerClient, publicKey: Uint8Array): RelayerSigner {
  const key = Uint8Array.from(publicKey);
  return { publicKey: key, sign: (bytes: Uint8Array) => client.feeSign(bytes) };
}
